import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, MessageCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { whatsappLink } from "@/lib/billing";
import { formatDate } from "@/lib/services";

const LeadCard = ({ l, onToggle, onDelete }) => (
  <div className={`rounded-xl border p-4 ${l.contacted ? "border-slate-800 bg-slate-900/60 opacity-70" : "border-slate-700/60 bg-slate-800/60"}`} data-testid={`lead-card-${l.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <div className="truncate font-semibold">{l.name}{l.company && <span className="font-normal text-slate-400"> · {l.company}</span>}</div>
        <div className="text-xs text-slate-400">{l.phone}{l.city ? ` · ${l.city}` : ""} · {formatDate(l.created_at.slice(0, 10))}</div>
      </div>
      {l.contacted && <span className="rounded-full border border-emerald-500/40 px-2 py-0.5 text-[11px] text-emerald-300">Contatado</span>}
    </div>
    {l.message && <p className="mt-2 text-sm text-slate-300">{l.message}</p>}
    <div className="mt-3 flex flex-wrap gap-2">
      <Button asChild size="sm" className="rounded-full bg-[#25D366] text-slate-950 hover:bg-[#1fb857]" data-testid={`lead-whatsapp-${l.id}`}>
        <a href={whatsappLink(l.phone, `Olá, ${l.name}! Aqui é do Ciclo Certo. Vi seu interesse e posso liberar seu acesso de teste.`)} target="_blank" rel="noreferrer"><MessageCircle className="mr-1 h-3.5 w-3.5" /> WhatsApp</a>
      </Button>
      <Button size="sm" variant="outline" className="rounded-full border-slate-700 bg-transparent" onClick={() => onToggle(l)} data-testid={`lead-toggle-${l.id}`}>
        <Check className="mr-1 h-3.5 w-3.5" /> {l.contacted ? "Marcar pendente" : "Marcar contatado"}
      </Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-400" onClick={() => onDelete(l)} data-testid={`lead-delete-${l.id}`} aria-label="Excluir"><Trash2 className="h-4 w-4" /></Button>
    </div>
  </div>
);

export const LeadsList = () => {
  const [leads, setLeads] = useState([]);
  const load = useCallback(() => api.get("/platform/leads").then((r) => setLeads(r.data)).catch((e) => toast.error(errorMessage(e))), []);
  useEffect(() => { load(); }, [load]);

  const act = (fn) => async (l) => {
    try { await fn(l); load(); } catch (err) { toast.error(errorMessage(err)); }
  };
  const toggle = act((l) => api.patch(`/platform/leads/${l.id}`, { contacted: !l.contacted }));
  const remove = act((l) => api.delete(`/platform/leads/${l.id}`));

  return (
    <section data-testid="leads-section">
      <h2 className="mb-3 font-display text-lg font-bold">Interessados da página de vendas <span className="font-mono text-sm text-slate-500">({leads.length})</span></h2>
      {leads.length === 0 && <p className="rounded-xl border border-dashed border-slate-700 py-8 text-center text-sm text-slate-500" data-testid="leads-empty">Nenhum contato ainda.</p>}
      <div className="grid gap-3 md:grid-cols-2">{leads.map((l) => <LeadCard key={l.id} l={l} onToggle={toggle} onDelete={remove} />)}</div>
    </section>
  );
};
